import React, { useRef, useState } from "react";
import Reveal from "./Reveal";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";

// Left video: short autoplay loop (showcase result)
const RESULT = {
  src: "/showcase/work-1.mp4",
  poster: "/showcase/work-1-poster.jpg",
  caption: "CarSpa Čestlice · Detailing live",
};

// Right video: long process video with audio
const PROCESS = {
  src: "/showcase/work-2.mp4",
  poster: "/showcase/work-2-poster.jpg",
  caption: "Průběh detailingu · 2:06",
};

const ResultVideo = () => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    if (!ref.current) return;
    const next = !muted;
    ref.current.muted = next;
    setMuted(next);
    if (!next) ref.current.play().catch(() => {});
  };

  return (
    <div
      data-testid="showcase-video-result"
      className="relative border-2 border-hormozi-yellow/60 hover:border-hormozi-yellow transition-colors bg-black mx-auto w-full max-w-xs sm:max-w-sm"
    >
      <video
        ref={ref}
        className="w-full h-auto block"
        src={RESULT.src}
        poster={RESULT.poster}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
      />
      <button
        type="button"
        onClick={toggle}
        data-testid="showcase-result-sound"
        aria-label={muted ? "Zapnout zvuk" : "Vypnout zvuk"}
        className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-2 bg-black/70 hover:bg-black backdrop-blur-sm border border-white/20 hover:border-hormozi-yellow text-white px-3 py-2 transition-colors"
      >
        {muted ? <VolumeX size={16} strokeWidth={1.6} /> : <Volume2 size={16} strokeWidth={1.6} className="text-hormozi-yellow" />}
        <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-bold">{muted ? "Zvuk" : "Ztlumit"}</span>
      </button>
      <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm px-3 py-1.5 border-l-2 border-hormozi-yellow">
        <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-bold text-white">
          {RESULT.caption}
        </span>
      </div>
    </div>
  );
};

const ProcessVideo = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  const togglePlay = () => {
    if (!ref.current) return;
    if (ref.current.paused) {
      ref.current.muted = false;
      setMuted(false);
      ref.current.play();
      setPlaying(true);
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!ref.current) return;
    const next = !muted;
    ref.current.muted = next;
    setMuted(next);
  };

  return (
    <div
      data-testid="showcase-video-process"
      className="relative border-2 border-hormozi-yellow/60 hover:border-hormozi-yellow transition-colors bg-black mx-auto w-full max-w-xs sm:max-w-sm group cursor-pointer"
      onClick={togglePlay}
    >
      <video
        ref={ref}
        className="w-full h-auto block"
        src={PROCESS.src}
        poster={PROCESS.poster}
        playsInline
        preload="metadata"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />

      {/* Big yellow play overlay when paused */}
      {!playing && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[1px]">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-hormozi-yellow flex items-center justify-center mb-3 transition-transform group-hover:scale-110">
            <Play size={28} strokeWidth={3} fill="black" className="text-black ml-1" />
          </div>
          <div className="bg-black/80 px-3 py-1.5 text-[10px] sm:text-[11px] tracking-wide uppercase font-black text-hormozi-yellow border border-hormozi-yellow">
            ▶ Přehrát se zvukem
          </div>
        </div>
      )}

      {/* Pause indicator on hover when playing */}
      {playing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="w-14 h-14 bg-black/70 border border-hormozi-yellow flex items-center justify-center">
            <Pause size={20} strokeWidth={2.4} className="text-hormozi-yellow" />
          </div>
        </div>
      )}

      {/* Sound toggle */}
      {playing && (
        <button
          type="button"
          onClick={toggleMute}
          data-testid="showcase-process-sound"
          aria-label={muted ? "Zapnout zvuk" : "Ztlumit"}
          className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-2 bg-black/70 hover:bg-black backdrop-blur-sm border border-white/20 hover:border-hormozi-yellow text-white px-3 py-2 transition-colors"
        >
          {muted ? <VolumeX size={16} strokeWidth={1.6} /> : <Volume2 size={16} strokeWidth={1.6} className="text-hormozi-yellow" />}
          <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-bold">{muted ? "Zvuk" : "Ztlumit"}</span>
        </button>
      )}

      <div className="absolute top-3 left-3 z-20 bg-black/80 backdrop-blur-sm px-3 py-1.5 border-l-2 border-hormozi-yellow">
        <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-bold text-white">
          {PROCESS.caption}
        </span>
      </div>
    </div>
  );
};

export const Showcase = () => {
  return (
    <section
      id="nase-prace"
      data-testid="showcase-section"
      className="relative py-16 sm:py-20 md:py-28 bg-black overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <Reveal className="max-w-3xl mb-8 sm:mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-hormozi-yellow px-3 py-1 mb-4">
            <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-black text-black">
              ▶ Naše práce naživo
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
            Tohle je <span className="text-hormozi-yellow">vaše auto.</span>
            <br />
            Po našem detailingu.
          </h2>
          <p className="mt-4 sm:mt-5 text-sm sm:text-base text-zinc-400 font-medium max-w-xl leading-relaxed">
            Žádné stock fotky. Žádné photoshop triky. Reálné záběry z naší dílny v Čestlicích —
            výsledek vlevo, <span className="text-white font-bold">průběh celého detailingu vpravo</span>.
          </p>
        </Reveal>

        <Reveal delay={150}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7 md:gap-8 mb-8 sm:mb-10">
            {/* Result */}
            <div className="space-y-3">
              <div className="text-[10px] sm:text-[11px] tracking-wide uppercase font-black text-hormozi-yellow text-center md:text-left">
                ▲ Výsledek
              </div>
              <ResultVideo />
            </div>

            {/* Process */}
            <div className="space-y-3">
              <div className="text-[10px] sm:text-[11px] tracking-wide uppercase font-black text-hormozi-yellow text-center md:text-left">
                ▲ Průběh detailingu (2:06 · se zvukem)
              </div>
              <ProcessVideo />
            </div>
          </div>
        </Reveal>

        <Reveal delay={250}>
          <div className="border-2 border-hormozi-yellow/60 bg-black/60">
            <div className="bg-hormozi-yellow text-black px-5 py-2.5 text-center">
              <span className="text-[11px] sm:text-xs tracking-wide uppercase font-black">
                ▲ Proč náš detailing dělá rozdíl
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
              {[
                "Profi polishery Flex a Rupes — ne amatérský rotak",
                "Chemie Koch (DE), Gyeon (KR), Swiss Wax (CH)",
                "Detailéři s 10+ lety praxe v oboru",
                "Sklokeramika s písemnou zárukou 2 roky",
              ].map((b, i) => (
                <div key={i} className="p-4 sm:p-5 flex items-start gap-2.5">
                  <span className="text-hormozi-yellow font-black text-base sm:text-lg leading-none mt-0.5 shrink-0">✓</span>
                  <p className="text-[13px] sm:text-sm text-zinc-200 leading-snug font-medium">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={350}>
          <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-3 sm:gap-5 max-w-2xl mx-auto">
            {[
              { stat: "12 000+", label: "ošetřených aut" },
              { stat: "10 let", label: "praxe" },
              { stat: "4,9 ★", label: "Google" },
            ].map((s) => (
              <div key={s.label} className="bg-black/40 border border-white/10 p-3 sm:p-4">
                <div className="font-display text-xl sm:text-2xl md:text-3xl text-hormozi-yellow leading-none tabular-nums">
                  {s.stat}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wide text-zinc-500 mt-1.5 sm:mt-2 font-bold">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Showcase;
